import React, { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import QuoteDialog from "@/components/QuoteDialog";

/**
 * Reusable service page hero used for each service page.
 */
export function ServicePageHero({
    eyebrow,
    title,
    subtitle,
    image,
    bullets = [],
    defaultQuoteService,
}) {
    const [quoteOpen, setQuoteOpen] = useState(false);
    return (
        <section className="relative overflow-hidden bg-[#FDFCFB]">
            <div className="container-x pt-14 md:pt-20 pb-16 md:pb-24 grid lg:grid-cols-[1.1fr_1fr] gap-12 lg:gap-16 items-center">
                <div className="reveal-up">
                    {eyebrow && (
                        <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                            <span className="gold-rule" />
                            {eyebrow}
                        </p>
                    )}
                    <h1 className="mt-5 font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-navy leading-[1.05]">
                        {title}
                    </h1>
                    <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-xl">
                        {subtitle}
                    </p>
                    {bullets.length > 0 && (
                        <ul className="mt-7 space-y-2.5">
                            {bullets.map((b) => (
                                <li key={b} className="flex items-start gap-3 text-slate-700">
                                    <CheckCircle2 className="h-5 w-5 text-[#C9A961] mt-0.5 flex-shrink-0" />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    <div className="mt-8 flex flex-wrap gap-3">
                        <button
                            data-testid="service-hero-quote-button"
                            onClick={() => setQuoteOpen(true)}
                            className="btn-gold"
                        >
                            Get a Free Quote <ArrowRight className="h-4 w-4" />
                        </button>
                        <Link
                            to="/contact"
                            data-testid="service-hero-contact-link"
                            className="btn-outline-navy"
                        >
                            Talk to an Expert
                        </Link>
                    </div>
                </div>
                <div className="relative reveal-up reveal-up-delay-2">
                    <div className="aspect-[4/5] w-full overflow-hidden rounded-[28px] bg-navy/5">
                        <img
                            src={image}
                            alt={title}
                            className="h-full w-full object-cover"
                            loading="lazy"
                        />
                    </div>
                    <div className="absolute -bottom-6 -left-6 hidden md:block bg-white rounded-2xl shadow-xl p-5 w-56 border border-gray-100">
                        <p className="text-xs font-semibold text-[#C9A961] uppercase tracking-wider">
                            Trusted across the GTA
                        </p>
                        <p className="mt-1.5 text-sm text-slate-700">
                            Fully Canadian-owned. Licensed & insured professionals.
                        </p>
                    </div>
                </div>
            </div>
            <QuoteDialog
                open={quoteOpen}
                onOpenChange={setQuoteOpen}
                defaultService={defaultQuoteService}
            />
        </section>
    );
}

export function PageHero({ eyebrow, title, subtitle }) {
    return (
        <section className="bg-navy text-white">
            <div className="container-x py-20 md:py-24">
                {eyebrow && (
                    <p className="inline-flex items-center text-xs font-semibold uppercase tracking-[0.18em] text-[#C9A961]">
                        <span className="gold-rule" /> {eyebrow}
                    </p>
                )}
                <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold max-w-3xl leading-[1.05]">
                    {title}
                </h1>
                {subtitle && (
                    <p className="mt-5 text-lg text-white/70 max-w-2xl leading-relaxed">
                        {subtitle}
                    </p>
                )}
            </div>
        </section>
    );
}

export function FeatureGrid({ items }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((item, idx) => {
                const Icon = item.icon;
                return (
                    <div
                        key={item.title}
                        data-testid={`feature-${idx}`}
                        className="card-premium p-8"
                    >
                        {Icon && (
                            <div className="h-12 w-12 rounded-xl bg-[#FDFCFB] border border-gold/30 text-gold flex items-center justify-center mb-5">
                                <Icon className="h-5 w-5" />
                            </div>
                        )}
                        <h3 className="font-display text-xl font-semibold text-navy">
                            {item.title}
                        </h3>
                        <p className="mt-2.5 text-slate-600 text-sm leading-relaxed">
                            {item.description}
                        </p>
                    </div>
                );
            })}
        </div>
    );
}

export function ProcessSteps({ steps }) {
    return (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-6 relative">
            {steps.map((s, i) => (
                <div key={s.title} className="relative">
                    <div className="text-xs font-semibold tracking-[0.2em] text-[#C9A961] uppercase">
                        Step 0{i + 1}
                    </div>
                    <h3 className="mt-3 font-display text-2xl font-semibold text-navy">
                        {s.title}
                    </h3>
                    <p className="mt-2.5 text-slate-600 text-sm leading-relaxed max-w-xs">
                        {s.description}
                    </p>
                </div>
            ))}
        </div>
    );
}

export function CTAStripe({ heading, subheading, ctaLabel, onCta }) {
    return (
        <section className="bg-navy">
            <div className="container-x py-16 md:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                    <h2 className="font-display text-3xl md:text-4xl font-bold text-white max-w-2xl">
                        {heading}
                    </h2>
                    {subheading && (
                        <p className="mt-3 text-white/70 max-w-xl">{subheading}</p>
                    )}
                </div>
                <button
                    data-testid="cta-stripe-button"
                    onClick={onCta}
                    className="btn-gold self-start md:self-auto whitespace-nowrap"
                >
                    {ctaLabel} <ArrowRight className="h-4 w-4" />
                </button>
            </div>
        </section>
    );
}
